import axios, { type AxiosError } from "axios";

const DEFAULT_IGNORED_ROOT_KEYS = new Set([
  "code",
  "message",
  "status",
  "statusCode",
]);

type ExtractOptions = {
  ignoredRootKeys?: ReadonlyArray<string> | Set<string>;
};

export const extractValidationErrors = (
  data: unknown,
  options: ExtractOptions = {},
): Record<string, string[]> => {
  const ignoredKeys = options.ignoredRootKeys
    ? new Set(options.ignoredRootKeys)
    : DEFAULT_IGNORED_ROOT_KEYS;

  const result: Record<string, string[]> = {};

  const visit = (node: unknown, path: string[] = []) => {
    if (node == null) return;

    if (typeof node === "string") {
      if (path.length === 0) return;
      if (path.length === 1 && ignoredKeys.has(path[0])) return;
      const key = path[path.length - 1];
      if (key) {
        result[key] = [...(result[key] ?? []), node];
      }
      return;
    }

    if (Array.isArray(node)) {
      const stringItems = node.filter(
        (item): item is string => typeof item === "string",
      );
      if (stringItems.length > 0) {
        const key = path[path.length - 1];
        if (key) {
          result[key] = [...(result[key] ?? []), ...stringItems];
          return;
        }
      }

      node.forEach((item) => {
        visit(item, path);
      });
      return;
    }

    if (typeof node === "object") {
      Object.entries(node).forEach(([childKey, value]) => {
        if (path.length === 0 && ignoredKeys.has(childKey)) {
          return;
        }
        visit(value, [...path, childKey]);
      });
    }
  };

  visit(data);
  return result;
};

export class ApiValidationError extends Error {
  fieldErrors: Record<string, string[]>;
  status: number;

  constructor(
    message: string,
    fieldErrors: Record<string, string[]>,
    status = 422,
  ) {
    super(message);
    this.name = "ApiValidationError";
    this.fieldErrors = fieldErrors;
    this.status = status;
  }
}

export class ApiHttpError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiHttpError";
    this.status = status;
  }
}

export const getApiErrorStatus = (error: unknown): number | null => {
  if (error instanceof ApiHttpError) {
    return error.status;
  }

  if (isAxiosError(error)) {
    return error.response?.status ?? null;
  }

  return null;
};

export const isForbiddenError = (error: unknown): boolean => {
  return getApiErrorStatus(error) === 403;
};

export const handleAxiosError = (
  error: AxiosError,
  defaultMessage: string,
  options: ExtractOptions = {},
): never => {
  if (!error.response) {
    throw new Error("Please check your connection");
  }

  if (error.response.status === 422 && error.response.data) {
    const fieldErrors = extractValidationErrors(error.response.data, options);
    const flattenedMessages = Object.values(fieldErrors).flat();
    const validationMessage =
      flattenedMessages[0] ||
      (typeof (error.response.data as { message?: unknown }).message ===
      "string"
        ? ((error.response.data as { message?: string }).message ??
          "Validation failed")
        : "Validation failed");

    throw new ApiValidationError(
      validationMessage,
      fieldErrors,
      error.response.status,
    );
  }

  const errorData = error.response.data as { message?: string } | undefined;
  throw new ApiHttpError(
    errorData?.message || defaultMessage,
    error.response.status,
  );
};

export const isAxiosError = (error: unknown): error is AxiosError =>
  axios.isAxiosError(error);
